<template>
  <div
    class="highlight-preview-controller-parts"
    :class="[
      `highlight-preview-controller-parts--${screenOrientationType}`,
      { 'highlight-preview-controller-parts--pc': isPc },
    ]"
  >
    <highlight-preview-seek-bar-parts
      v-if="videoTimesData"
      class="highlight-preview-controller-parts__seek-bar"
      v-model="videoTimesData"
    />

    <div class="highlight-preview-controller-parts__buttons">
      <!-- 再生 -->
      <button
        v-if="videoPausedStatus"
        class="highlight-preview-controller-parts__button highlight-preview-controller-parts__button__play"
        @click="playVideo"
      >
        <img :src="require('@/assets/img/icon/video_controller_play.svg')" alt="" />
      </button>

      <!-- 一時停止 -->
      <button
        v-if="!videoPausedStatus"
        class="highlight-preview-controller-parts__button highlight-preview-controller-parts__button__pause"
        @click="pauseVideo"
      >
        <img :src="require('@/assets/img/icon/video_controller_pause.svg')" alt="" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import HighlightPreviewSeekBarParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/HighlightPreviewSeekBarParts.vue'
import { VideoStatusType } from '@/components/hook/useVideoPlayer'
import DeviceInfo from '@/util/DeviceInfo'
/**
 * ハイライト作成モーダル、プレビュー表示の動画コントローラーパーツ
 */
export default defineComponent({
  name: 'HighlightPreviewControllerParts',
  components: {
    HighlightPreviewSeekBarParts,
  },
  props: {
    /**
     * シークバー用再生時間データ
     */
    videoTimes: {
      type: Object as PropType<VideoStatusType>,
      required: false,
    },
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * ビデオ一時停止フラグ
     */
    videoPaused: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const videoTimesData = computed({
      get: () => props.videoTimes,
      set: (val) => context.emit('updateCurrentTime', val?.currentTime),
    })
    const videoPausedStatus = computed(() => props.videoPaused)

    const isPc = DeviceInfo.isForPC()

    return {
      isPc,
      videoTimesData,
      videoPausedStatus,
    }
  },
  methods: {
    /**
     * ビデオ 再生 処理
     */
    playVideo(): void {
      this.$emit('videoPlayPauseChange', false)
    },

    /**
     * ビデオ 一時停止 処理
     */
    pauseVideo(): void {
      this.$emit('videoPlayPauseChange', true)
    },
  },
})
</script>

<style lang="scss" scoped>
.highlight-preview-controller-parts {
  position: absolute;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px 0 calc(50% + 66px);
  background: rgba(0, 0, 0, 70%);

  &::before {
    display: block;
    content: '';
    width: 0;
    height: 1px;
  }

  @media screen and (max-width: 319px) {
    padding: 8px 6px 0 calc(50% + 66px);
  }

  @media screen and (max-width: 290px) {
    padding: 8px 10px 0 18px;

    &::before {
      display: none;
    }
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &--landscape-primary,
  &--landscape-secondary {
    justify-content: flex-end;
    padding: 0;
    background: none;
    gap: 0;

    @media screen and (max-width: 710px) {
      padding-right: 12px;
    }

    &::before {
      position: fixed;
      left: 50%;
      bottom: 0;
      display: block;
      content: '';
      width: 100vw;
      height: 78px;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%);
      z-index: 0;
      transform: translateX(-50%);
    }

    > *:not(.seek-bar-parts) {
      position: relative;
      z-index: 1;
    }

    .highlight-preview-controller-parts {
      &__buttons {
        position: absolute;
        left: 42%;
        top: -100px;
        transform: translateX(calc(-50% + 26px));
      }

      &__button {
        width: 36px;
        height: 36px;
        border-radius: 2px;
        margin-left: 0;

        svg {
          width: 24px;
        }

        &__play,
        &__pause {
          width: 32px;
          height: 32px;

          svg {
            width: 48px;
          }
        }
      }
    }

    @media screen and (max-width: 650px) {
      .highlight-preview-controller-parts {
        &__buttons {
          transform: translateX(-50%);
        }

        &__button {
          &__play,
          &__pause {
            margin-left: 6px;
          }
        }
      }
    }
  }

  &--pc {
    top: 0;
    height: 100%;

    .highlight-preview-controller-parts__seek-bar {
      position: absolute;
      top: auto;
      bottom: 0;
    }

    .highlight-preview-controller-parts__buttons {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
